import { Datetime, Schema } from '@/utils';
import {
  DispatchIssueDocument,
  DispatchIssueFlightLateEnum,
  DispatchIssueFlightTypeEnum,
  DispatchIssueRecoveryCompletedEnum,
  DispatchIssueRecoveryNeededEnum,
  DispatchIssueRootCauseEnum,
  DispatchIssueTaxiDetailsEnum,
  DispatchIssueTaxiUsedEnum,
  DispatchIssueWithCache,
  Trip,
} from '@/models/gen/graphql';
import { clearFormStorage, getFormStorage, setFormStorage } from '@/common/Form';

export namespace IssueHistoryFormDefinitions {
  export type Locale =
    | 'SUBMIT_BUTTON_TEXT'
    | 'SUBMIT_BUTTON_COLOR'
    | 'Dispatch Issues'
    | 'Station'
    | 'Root Cause'
    | 'Root Cause Notes'
    | 'Issue Date/Time'
    | 'Flight Type'
    | 'Description Of Flight Issue'
    | 'Dispatcher'
    | 'Caused By Dispatcher'
    | 'Caused By'
    | 'Dispatcher Who Caused Issue'
    | 'Did The Plane Push Late?'
    | 'Used Taxi/Lyft?'
    | 'Cost Of Taxi/Lyft'
    | 'Recovery Flight #'
    | 'Recovery Date/Time'
    | 'Outbound Flight Information'
    | 'Which Mod Notified For Recovery'
    | 'Crew Member Name'
    | 'Crew Member Phone'
    | 'Issue Summary'
    | 'Driver Name'
    | 'Driver Phone'
    | 'Detailed Explanation'
    | 'Is Recovery Needed?'
    | 'Is Recovery Complete?'
    | 'Taxi Details'
    | '# Of Crew For Gift Card'
    | 'Create Dispatch Issue'
    | 'Dispatch Issue History'
    | '0'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | '10'
    | 'Crew Paid'
    | 'SHG Paid'
    | 'SHG Taxi'
    | 'Must not exceed 64 characters'
    | 'Arrival'
    | 'Departure'
    | 'PUSH TIME'
    | 'SCHED TIME'
    | 'Van Number';
  export type Value = Partial<DispatchIssueWithDocuments>;
  export type Props = {
    onSubmit?: (data: Value) => void | Value | Promise<Value> | undefined;
    onEditDispatchIssue?: (data: Value) => void | Value | Promise<Value> | undefined;
    locale?: Partial<Record<Locale, string>>;
    value?: Partial<Value>;
    trip?: Partial<Trip>;
  };
  export const initValue: Value = {
    crewMemberGiftCardCount: 0,
    crewMemberName: '',
    crewMemberPhone: '',
    datetime: '',
    dispatcherId: '',
    driverPhone: '',
    driverId: '',
    flightIssueDescription: '',
    flightLate: undefined,
    managerId: '',
    outboundFlightInfo: '',
    recoveryCompleted: undefined,
    recoveryFlightDatetime: '',
    recoveryFlightNumber: 0,
    recoveryNeeded: undefined,
    rootCause: undefined,
    rootCauseDescription: '',
    station: '',
    taxiCost: 0,
    taxiDetails: undefined,
    taxiUsed: undefined,
    tripId: '',
    scheduledTime: undefined,
    pushTime: undefined,
    recoveryFlightName: undefined,
    flightType: undefined,
    causedBy: [],
    causedByForm: {},
  };
  export const getLocale = (value: Value): Partial<Record<Locale, string>> => ({
    SUBMIT_BUTTON_TEXT: value.id ? 'Save Issue' : 'Create Issue',
    SUBMIT_BUTTON_COLOR: value.id ? 'primary' : 'success',
  });
  export const validator = (locale) =>
    Schema((z) =>
      z.object({
        crewMemberGiftCardCount: z
          .any()
          .refine((val: string = '0'): boolean => parseFloat(val) >= 0, { message: locale('Must be a positive number') }),
        crewMemberName: z.string(),
        crewMemberPhone: z.string(),
        datetime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString(), { message: locale('Required') }),
        dispatcherId: z.string().uuid({ message: locale('Required') }),
        driverId: z.string().uuid({ message: locale('Required') }),
        driverPhone: z.string(),
        flightIssueDescription: z.string().min(1, { message: locale('Required') }),
        flightLate: z.nativeEnum(DispatchIssueFlightLateEnum, { message: locale('Required') }),
        managerId: z.string().uuid({ message: locale('Required') }),
        outboundFlightInfo: z.string().min(1, { message: locale('Required') }),
        recoveryCompleted: z.nativeEnum(DispatchIssueRecoveryCompletedEnum, { message: locale('Required') }),
        recoveryFlightDatetime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        recoveryNeeded: z.nativeEnum(DispatchIssueRecoveryNeededEnum, { message: locale('Required') }),
        rootCause: z.nativeEnum(DispatchIssueRootCauseEnum, { message: locale('Required') }),
        rootCauseDescription: z.string().min(1, { message: locale('Required') }),
        station: z.string().min(1, { message: locale('Required') }),
        taxiCost: z.string().refine((val: string = '0'): boolean => parseFloat(val) >= 0, { message: locale('Must be a positive number') }),
        taxiDetails: z.nativeEnum(DispatchIssueTaxiDetailsEnum, { message: locale('Required') }),
        taxiUsed: z.nativeEnum(DispatchIssueTaxiUsedEnum, { message: locale('Required') }),
        tripId: z.string().uuid({ message: locale('Required') }),
        recoveryFlightName: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        flightType: z.nativeEnum(DispatchIssueFlightTypeEnum, { message: locale('Required') }),
        vehicleId: z.string().uuid({ message: locale('Required') }),
        scheduledTime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        pushTime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        causedByForm: z.record(z.string().nonempty('Required')).default({}),
      })
    );
  export const searchable = {
    station: (val) => val,
    datetime: (val) => val,
    tripServicerIataAirlineCode: (val) => val,
    tripFlightNumber: (val) => val,
    driverFirstName: (val, full) => [val, full?.driverLastName || ''].join(' ').trim(),
    rootCause: (val) => val,
    taxiUsed: (val) => val,
    taxiCost: (val) => val,
    flightLate: (val) => val,
    tripLoopName: (val) => val,
  };
  export const saveForm = (values: Value): void => setFormStorage('DispatchIssueHistoryForm', values);
  export const loadForm = (): Value => getFormStorage('DispatchIssueHistoryForm');
  export const clearForm = (): void => clearFormStorage('DispatchIssueHistoryForm');
}
export type DispatchIssueWithDocuments = DispatchIssueWithCache & {
  documents: DispatchIssueDocument[];
  causedByForm: Record<string, string>;
};
